import * as B from '@blueprintjs/core';
import React from 'react';

import {AlertPolicy} from 'app/modules/Remote/Feature/types';
import {
  CUSTOM_ALERT_TYPE_COMPOSITE,
  CUSTOM_ALERT_TYPE_VEGETATION_DROP,
  SOURCE_DETAILS_BY_ID,
} from 'app/utils/constants';
import * as layerUtils from 'app/utils/layerUtils';

import {DatasetSelect} from '../DatasetSelect';
import cs from '../styles.module.scss';
import {AlertFormAction, AlertFormState} from '../useAlertForm';

const sourceLabel = (sourceId: string) => {
  const details = SOURCE_DETAILS_BY_ID[sourceId as keyof typeof SOURCE_DETAILS_BY_ID] as
    | {name: string; operator?: string}
    | undefined;
  if (!details) return sourceId;
  return details.operator ? `${details.name} (${details.operator})` : details.name;
};

/**
 * The sources a composite can be built on, and the numeric layer each one leads with.
 *
 * availableLayerKeys is the layer menu's list, which holds more than the layers a rule can be built
 * on: category layers, and grouped high-res keys standing for whichever source has data. Neither can
 * name the one source a composite reads its conditions off, so a portfolio can yield no sources at
 * all — the caller has to handle that rather than offer composite.
 */
export const compositeSourcesFromLayerKeys = (availableLayerKeys: string[]) => {
  const firstLayerKeyBySourceId: Record<string, string> = {};
  for (const layerKey of availableLayerKeys) {
    if (layerUtils.isLayerKeyGroupedHighResData(layerKey)) continue;
    if (layerUtils.getLayer(layerKey).type !== 'data') continue;
    const {sourceId} = layerUtils.parseLayerKey(layerKey);
    if (!(sourceId in firstLayerKeyBySourceId)) firstLayerKeyBySourceId[sourceId] = layerKey;
  }
  const compositeSourceOptions = Object.keys(firstLayerKeyBySourceId).map((sourceId) => ({
    value: sourceId,
    label: sourceLabel(sourceId),
  }));
  return {compositeSourceOptions, firstLayerKeyBySourceId};
};

export const LayerAlertStep: React.FC<{
  disableInputs: boolean;
  formState: AlertFormState;
  dispatch: React.Dispatch<AlertFormAction>;
  updateMode: boolean;
  alertPolicy?: AlertPolicy;
  availableLayerKeys: string[];
  showCompositeOption: boolean;
}> = React.memo(
  ({
    formState,
    dispatch,
    disableInputs,
    updateMode,
    alertPolicy,
    availableLayerKeys,
    showCompositeOption,
  }) => {
    const isVegetationDrop = formState.alertType === CUSTOM_ALERT_TYPE_VEGETATION_DROP;
    const isComposite = formState.alertType === CUSTOM_ALERT_TYPE_COMPOSITE;
    const showVegetationDropOption = !updateMode;
    const [alertTypeOption, setAlertTypeOption] = React.useState<
      'vegetation-drop' | 'custom-layer' | 'composite'
    >(isVegetationDrop ? 'vegetation-drop' : isComposite ? 'composite' : 'custom-layer');

    // A composite picks one source up front — its conditions are checked on the same pixel of the
    // same scene — and each condition then picks a layer of that source.
    const {compositeSourceOptions, firstLayerKeyBySourceId} = React.useMemo(
      () => compositeSourcesFromLayerKeys(availableLayerKeys),
      [availableLayerKeys]
    );

    React.useEffect(() => {
      if (isVegetationDrop && alertTypeOption !== 'vegetation-drop') {
        setAlertTypeOption('vegetation-drop');
      } else if (isComposite && alertTypeOption !== 'composite') {
        setAlertTypeOption('composite');
      } else if (
        !isVegetationDrop &&
        !isComposite &&
        alertTypeOption !== 'custom-layer' &&
        !updateMode
      ) {
        setAlertTypeOption('custom-layer');
      }
    }, [isVegetationDrop, isComposite, alertTypeOption, updateMode]);

    return (
      <div className={cs.dialogPanel}>
        <B.Callout intent={B.Intent.PRIMARY} icon={null}>
          Lookouts help you detect meaningful changes automatically. Choose what to monitor, where,
          and when — and we'll alert you when conditions match your rules.
        </B.Callout>

        {!updateMode && showVegetationDropOption && (
          <B.FormGroup label="Choose type of Lookout to customize">
            <B.RadioGroup
              selectedValue={alertTypeOption}
              onChange={({currentTarget: {value}}) => {
                const option = value as 'vegetation-drop' | 'custom-layer' | 'composite';
                setAlertTypeOption(option);
                if (option === 'vegetation-drop') {
                  dispatch({type: 'SET_ALERT_TYPE', alertType: CUSTOM_ALERT_TYPE_VEGETATION_DROP});
                } else if (option === 'composite') {
                  dispatch({
                    type: 'SET_ALERT_TYPE',
                    alertType: CUSTOM_ALERT_TYPE_COMPOSITE,
                    // A range condition needs a numeric layer, and the first available layer can be
                    // a category one.
                    layerKey: Object.values(firstLayerKeyBySourceId)[0],
                  });
                } else if (option === 'custom-layer' && availableLayerKeys.length > 0) {
                  // Automatically select first available layer when switching to custom-layer
                  const firstLayer = layerUtils.getLayer(availableLayerKeys[0]);
                  dispatch({type: 'SET_LAYER', layer: firstLayer});
                }
              }}
              disabled={disableInputs}
            >
              <B.Radio label="Unseasonal vegetation drop" value="vegetation-drop" />
              <B.Radio label="Custom based on layer" value="custom-layer" />
              {showCompositeOption && compositeSourceOptions.length > 0 && (
                <B.Radio label="Custom based on multiple layers" value="composite" />
              )}
            </B.RadioGroup>
          </B.FormGroup>
        )}

        {alertTypeOption === 'composite' && (
          <>
            <B.Callout>
              Conditions are checked on the same pixel of the same scene, so they all read from one
              source. You'll pick each condition's layer on the "Set conditions" step.
            </B.Callout>
            {/* DatasetSelect presents layers (it renders each item's LayerInfo.display and links
                the layer library), not sources, so it isn't a fit here — a composite source has
                no single LayerInfo to show. Sized to match the Select dataset control below. */}
            <B.FormGroup
              label={
                <div className={cs.datasetLabelContainer}>
                  <span>Select source</span>
                  {updateMode && (
                    <B.Tooltip
                      content={
                        <div className={cs.datasetTooltipContent}>
                          <p>
                            Every condition reads a layer of this source, so it can't change once
                            the lookout exists. To monitor a different source, create a new lookout.
                          </p>
                        </div>
                      }
                      position={B.Position.BOTTOM}
                    >
                      <B.Icon icon="info-sign" size={12} className={cs.datasetInfoIcon} />
                    </B.Tooltip>
                  )}
                </div>
              }
            >
              <div className={cs.datasetSelectContainer}>
                <B.HTMLSelect
                  value={layerUtils.parseLayerKey(formState.layer.key).sourceId}
                  disabled={disableInputs || updateMode}
                  options={compositeSourceOptions}
                  onChange={({currentTarget: {value}}) =>
                    dispatch({
                      type: 'SET_ALERT_TYPE',
                      alertType: CUSTOM_ALERT_TYPE_COMPOSITE,
                      layerKey: firstLayerKeyBySourceId[value],
                    })
                  }
                />
              </div>
            </B.FormGroup>
          </>
        )}

        {alertTypeOption !== 'composite' &&
          (alertTypeOption === 'custom-layer' || !showVegetationDropOption) && (
            <B.FormGroup
              label={
                <div className={cs.datasetLabelContainer}>
                  <span>Select dataset</span>
                  {updateMode && alertPolicy && (
                    <B.Tooltip
                      content={
                        <div className={cs.datasetTooltipContent}>
                          <p>
                            You're editing a {alertPolicy.alertType} lookout. Only{' '}
                            {alertPolicy.alertType} datasets can be selected. To choose a different
                            type of dataset, create a new lookout.
                          </p>
                        </div>
                      }
                      position={B.Position.BOTTOM}
                    >
                      <B.Icon icon="info-sign" size={12} className={cs.datasetInfoIcon} />
                    </B.Tooltip>
                  )}
                </div>
              }
            >
              <div className={cs.datasetSelectContainer}>
                <DatasetSelect
                  onItemSelect={(layerKey) => {
                    const newLayer = layerUtils.getLayer(layerKey);
                    dispatch({type: 'SET_LAYER', layer: newLayer});
                    // Update local state to reflect layer selection
                    setAlertTypeOption('custom-layer');
                  }}
                  activeLayerInfo={formState.layer}
                  availableLayerKeys={
                    updateMode && alertPolicy
                      ? availableLayerKeys.filter((layerKey) => {
                          const layer = layerUtils.getLayer(layerKey);
                          const layerType = layer.type === 'data' ? 'threshold' : 'category';
                          return layerType === alertPolicy.alertType;
                        })
                      : availableLayerKeys
                  }
                  disabled={disableInputs}
                />
              </div>
            </B.FormGroup>
          )}

        {alertTypeOption === 'vegetation-drop' && (
          <B.Callout>
            Unseasonal vegetation drop alerts detect major declines using Sentinel-2 vegetation
            data.
          </B.Callout>
        )}
      </div>
    );
  }
);
